import React, { useContext, useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import StepperForm from "../common/StepperForm";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import Step6 from "./Step6";
import StepKeyContacts from "./StepKeyContacts";
import StepFinalQualification from "./StepFinalQualification";
import { Box, Typography } from "@mui/material";
import Submitted from "assets/icons/Submitted.svg";
import {
  formFields,
  Title,
  initialValues,
  validationSchemas,
  stepsNames,
} from "../../constant/Home/VendorOnboarding";
import { stepFieldsMap } from "../../constant/Home/VendorOnboarding";
import { Vendor } from "../../../service";
import useApiHandler from "../../hooks/ApiHandler";
import Cookies from "universal-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { useFormikContext } from "formik";

const FormObserver = () => {
  const { values } = useFormikContext();
  useEffect(() => {
    // Save draft to sessionStorage
    sessionStorage.setItem("vendor_onboarding_draft", JSON.stringify(values));
  }, [values]);
  return null;
};

const VendorOnboarding = () => {
  const { callApi } = useApiHandler();
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine initial step from URL (?step=) using 0-based index and clamp to [0, submitStep]
  const submitStep = 6; // last interactive step index
  const getStepFromSearch = (search) => {
    const params = new URLSearchParams(search);
    const stepParam = params.get("step");
    const parsed = parseInt(stepParam, 10);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, Math.min(parsed, submitStep));
  };

  const [activeStep, setActiveStep] = useState(() => getStepFromSearch(location.search));
  const [formInitialValues, setFormInitialValues] = useState(initialValues);

  // State for reference management
  const [showAddReference, setShowAddReference] = useState(false);
  const [showAddLicense, setShowAddLicense] = useState(false);
  const [showAddInsurance, setShowAddInsurance] = useState(false);

  // Track if any form is open to prevent navigation
  const hasOpenForm = showAddLicense || showAddInsurance;

  // Helper to map backend snapshot -> form values
  const mapSnapshotToFormValues = (snap, current) => {
    const toIdArray = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr
        .map((item) => (typeof item === "number" ? item : item?.id))
        .filter((v) => v != null);
    };

    const mapYearsToBusinessDuration = (years) => {
      // Accept either a code or numeric; return one of the select options
      if (!years && years !== 0) return "";
      if (["LT1", "1_3", "3_5", "5_10", "10P"].includes(years)) {
        switch (years) {
          case "LT1": return "less_than_1_year";
          case "1_3": return "1_2_years";
          case "3_5": return "3_5_years";
          case "5_10": return "6_10_years";
          case "10P": return "more_than_10_years";
          default: return "";
        }
      }
      if (typeof years === "string") return years; // fallback
      const n = Number(years);
      if (Number.isNaN(n)) return "";
      if (n < 1) return "less_than_1_year";
      if (n <= 2) return "1_2_years";
      if (n <= 5) return "3_5_years";
      if (n <= 10) return "6_10_years";
      return "more_than_10_years";
    };

    // Prefer server values; fall back to current form state, then userData, then defaults
    const resolvedEmail =
      snap?.email ?? current?.email ?? userData?.email ?? initialValues.email;
    const resolvedFullName =
      snap?.full_name ??
      snap?.contact_full_name ??
      current?.fullName ??
      userData?.fullName ??
      userData?.full_name ??
      initialValues.fullName;

    return {
      ...initialValues,
      // Step 1: Basic Contact Info
      fullName: resolvedFullName,
      phone_number: snap?.phone_number ?? current?.phone_number ?? "",
      email: resolvedEmail,

      // Step 2: Business Details
      company_name: snap?.company_name ?? current?.company_name ?? "",
      business_address: snap?.business_address_line1 ?? current?.business_address ?? "",
      business_city: snap?.business_city ?? current?.business_city ?? "",
      business_state: snap?.business_state ?? current?.business_state ?? "",
      business_zip: snap?.business_zip ?? current?.business_zip ?? "",
      service_category_ids: toIdArray(snap?.service_categories).length > 0 ? toIdArray(snap?.service_categories) : (current?.service_category_ids || []),
      county_ids: toIdArray(snap?.vendor_service_counties).length > 0 ? toIdArray(snap?.vendor_service_counties) : (current?.county_ids || []),
      year_started: snap?.year_started ?? current?.year_started ?? "",
      years_under_present_name: snap?.years_under_present_name ?? current?.years_under_present_name ?? "",
      operated_under_other_names: snap?.operated_under_other_names ? "yes" : (snap?.operated_under_other_names === false ? "no" : ""),
      prior_names: snap?.prior_names ?? current?.prior_names ?? "",
      organization_type: snap?.organization_type === "INDIVIDUAL" ? "SOLE_PROPRIETORSHIP" : (snap?.organization_type ?? current?.organization_type ?? ""),
      organization_state: snap?.organization_state ?? current?.organization_state ?? "",
      organization_other_description: snap?.organization_other_description ?? current?.organization_other_description ?? "",
      partnership_type: snap?.partnership_type ?? current?.partnership_type ?? "",
      business_website: snap?.business_website ?? current?.business_website ?? "",
      main_phone: snap?.main_phone ?? current?.main_phone ?? "",
      main_fax: snap?.main_fax ?? current?.main_fax ?? "",
      ownership_changes_past_year: snap?.ownership_changes_past_year ? "yes" : (snap?.ownership_changes_past_year === false ? "no" : ""),
      ownership_changes_details: snap?.ownership_changes_details ?? current?.ownership_changes_details ?? "",

      // Step 3: Key Contacts
      business_contact_name: snap?.business_contact_name ?? current?.business_contact_name ?? "",
      business_contact_email: snap?.business_contact_email ?? current?.business_contact_email ?? "",
      business_contact_phone: snap?.business_contact_phone ?? current?.business_contact_phone ?? "",
      insurance_contact_name: snap?.insurance_contact_name ?? current?.insurance_contact_name ?? "",
      insurance_contact_email: snap?.insurance_contact_email ?? current?.insurance_contact_email ?? "",
      insurance_contact_phone: snap?.insurance_contact_phone ?? current?.insurance_contact_phone ?? "",
      accounting_contact_name: snap?.accounting_contact_name ?? current?.accounting_contact_name ?? "",
      accounting_contact_email: snap?.accounting_contact_email ?? current?.accounting_contact_email ?? "",
      accounting_contact_phone: snap?.accounting_contact_phone ?? current?.accounting_contact_phone ?? "",
      same_as_business_insurance: current?.same_as_business_insurance ?? false,
      same_as_business_accounting: current?.same_as_business_accounting ?? false,

      // Step 4: Licensing & Insurance
      licenses: (Array.isArray(snap?.licenses) && snap.licenses.length > 0)
        ? snap.licenses.map(l => ({
            ...l,
            // Keep the document reference - don't null it out
            license_document: l.license_document || null,
            license_document_url: l.license_document_url || l.license_document || ""
          }))
        : (Array.isArray(current?.licenses) && current.licenses.length > 0) ? current.licenses : [],
      insurances: (Array.isArray(snap?.insurances) && snap.insurances.length > 0)
        ? snap.insurances.map(i => ({
            ...i,
            // Keep the document reference - don't null it out
            insurance_document: i.insurance_document || null,
            insurance_document_url: i.insurance_document_url || i.insurance_document || ""
          }))
        : (Array.isArray(current?.insurances) && current.insurances.length > 0) ? current.insurances : [],
      // emergency_24_7: snap?.emergency_24_7 ?? current?.emergency_24_7 ?? false,

      // Step 5: Stripe
      stripe_onboarding_complete:
        snap?.stripe &&
        (snap.stripe.onboarding_status === "complete" ||
          snap.stripe.ready === true ||
          snap.stripe.charges_enabled === true)
          ? true
          : snap?.stripe_onboarding_complete === true
          ? true
          : current?.stripe_onboarding_complete ??
            initialValues.stripe_onboarding_complete,
      stripe_onboarding_status: snap?.stripe?.onboarding_status || "",
      stripe_charges_enabled: !!snap?.stripe?.charges_enabled,
      stripe_payouts_enabled: !!snap?.stripe?.payouts_enabled,
      stripe_ready: !!snap?.stripe?.ready,

      // Step 6: References
      businessDuration: mapYearsToBusinessDuration(snap?.years_in_business ?? current?.years_in_business),
      references: (Array.isArray(snap?.references) && snap.references.length > 0)
        ? snap.references
        : (Array.isArray(current?.references) && current.references.length > 0) ? current.references : [],

      // Step 7: Business History
      is_bonded: snap?.is_bonded ? "yes" : (snap?.is_bonded === false ? "no" : ""),
      bond_amount: snap?.bond_amount ?? current?.bond_amount ?? "",
      bond_expiration_date: snap?.bond_expiration_date ?? current?.bond_expiration_date ?? "",
      safety_violations_past_5_years: snap?.safety_violations_past_5_years ? "yes" : (snap?.safety_violations_past_5_years === false ? "no" : ""),
      safety_violations_details: snap?.safety_violations_details ?? current?.safety_violations_details ?? "",
      pending_claims_suits: snap?.pending_claims_suits ? "yes" : (snap?.pending_claims_suits === false ? "no" : ""),
      pending_claims_suits_details: snap?.pending_claims_suits_details ?? current?.pending_claims_suits_details ?? "",
      felony_convictions: snap?.felony_convictions ? "yes" : (snap?.felony_convictions === false ? "no" : ""),
      felony_convictions_details: snap?.felony_convictions_details ?? current?.felony_convictions_details ?? "",
      // max_project_size: snap?.max_project_size ?? current?.max_project_size ?? "",
      // total_annual_volume: snap?.total_annual_volume ?? current?.total_annual_volume ?? "",
      // number_of_employees: snap?.number_of_employees ?? current?.number_of_employees ?? "",
      certifications: snap?.certifications ?? current?.certifications ?? "",
      offers_warranty: snap?.offers_warranty ? "yes" : (snap?.offers_warranty === false ? "no" : ""),
      warranty_duration_text: snap?.warranty_duration_text ?? current?.warranty_duration_text ?? "",
      // w9_file: snap?.payout?.w9_file || current?.w9_file || null,
    };
  };

  // Load existing onboarding snapshot to prefill and allow resuming
  useEffect(() => {
    const loadSnapshot = async () => {
      // Check for draft in sessionStorage first
      const draft = sessionStorage.getItem("vendor_onboarding_draft");
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          setFormInitialValues((prev) => ({ ...prev, ...parsed }));
        } catch (e) {
          console.error("Error parsing vendor_onboarding_draft:", e);
        }
      }

      try {
        await callApi(() => Vendor.getVendorOnboardingMe(), {
          onSuccess: (res) => {
            const data = res?.data || {};
            // If we have a draft, we use it as the 'current' values to merge with server snapshot
            const currentValues = draft ? JSON.parse(draft) : formInitialValues;
            const mapped = mapSnapshotToFormValues(data, currentValues);
            setFormInitialValues(mapped);
          },
        });
      } catch (e) {
        // Ignore and use defaults
      }
    };
    loadSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [newReference, setNewReference] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const [newLicense, setNewLicense] = useState({
    license_name: "",
    issuing_authority: "",
    jurisdiction_coverage: "",
    license_number: "",
    license_document: null,
    expiration_date: "",
  });

  const [newInsurance, setNewInsurance] = useState({
    insurance_document: null,
  });

  // Removed automatic redirect to dashboard - this should be handled by ProtectedRoute

  // Reference management functions
  const handleAddReference = (setFieldValue, values) => {
    const updatedReferences = [...(values.references || []), newReference];
    setFieldValue("references", updatedReferences);
    setNewReference({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
    setShowAddReference(false);
  };

  const handleRemoveReference = (index, setFieldValue, values) => {
    const updatedReferences = values.references.filter((_, i) => i !== index);
    setFieldValue("references", updatedReferences);
  };

  const handleCancelAdd = () => {
    setNewReference({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
    setShowAddReference(false);
  };

  const handleInputChange = (field, value) => {
    setNewReference((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLicenseInputChange = (field, value) => {
    setNewLicense((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddLicense = (setFieldValue, values) => {
    const updatedLicenses = [...(values.licenses || []), newLicense];
    setFieldValue("licenses", updatedLicenses);
    setNewLicense({
      license_name: "",
      issuing_authority: "",
      jurisdiction_coverage: "",
      license_number: "",
      license_document: null,
      expiration_date: "",
    });
    setShowAddLicense(false);
  };

  const handleRemoveLicense = (index, setFieldValue, values) => {
    const updatedLicenses = values.licenses.filter((_, i) => i !== index);
    setFieldValue("licenses", updatedLicenses);
  };

  const handleAddInsurance = (setFieldValue, values) => {
    const updatedInsurances = [...(values.insurances || []), newInsurance];
    setFieldValue("insurances", updatedInsurances);
    setNewInsurance({
      insurance_document: null,
    });
    setShowAddInsurance(false);
  };

  const handleRemoveInsurance = (index, setFieldValue, values) => {
    const updatedInsurances = values.insurances.filter((_, i) => i !== index);
    setFieldValue("insurances", updatedInsurances);
  };

  const activeSteps = {
    0: <Step1 fields={formFields.step1} />,
    1: <Step2 fields={formFields.step2} />,
    2: <StepKeyContacts fields={formFields.step4} />,
    3: (
      <Step3
        fields={formFields.step3}
        showAddLicense={showAddLicense}
        setShowAddLicense={setShowAddLicense}
        newLicense={newLicense}
        handleLicenseInputChange={handleLicenseInputChange}
        handleAddLicense={handleAddLicense}
        handleRemoveLicense={handleRemoveLicense}
        showAddInsurance={showAddInsurance}
        setShowAddInsurance={setShowAddInsurance}
        newInsurance={newInsurance}
        handleAddInsurance={handleAddInsurance}
        handleRemoveInsurance={handleRemoveInsurance}
        setNewInsurance={setNewInsurance}
      />
    ),
    4: <Step5 />,
    5: (
      <Step6
        fields={formFields.step6}
        showAddReference={showAddReference}
        setShowAddReference={setShowAddReference}
        newReference={newReference}
        handleAddReference={handleAddReference}
        handleRemoveReference={handleRemoveReference}
        handleCancelAdd={handleCancelAdd}
        handleInputChange={handleInputChange}
      />
    ),
    6: <StepFinalQualification fields={{ ...formFields.step7, ...formFields.step5 }} />,
    7: null, // Completion screen
  };

  const cookies = new Cookies();
  const token = cookies.get("token");
  // Helper: transform form keys to API keys for a given step
  const transformForApi = (stepIndex, partial) => {
    const out = { ...partial };

    // Common boolean transformations for all steps (handles partial/full)
    const boolFields = [
      "operated_under_other_names",
      "ownership_changes_past_year",
      "is_bonded",
      "safety_violations_past_5_years",
      "pending_claims_suits",
      "felony_convictions",
      "offers_warranty"
    ];
    boolFields.forEach(f => {
      if (out[f] === "yes") out[f] = true;
      else if (out[f] === "no") out[f] = false;
      // Don't convert empty/null to false - keep as undefined so backend ignores it for partial saves
      // Only send boolean values when user has made an explicit selection
      else if (out[f] === "" || out[f] === undefined || out[f] === null) delete out[f];
    });

    // Convert integer fields: empty strings to null, string numbers to integers
    const intFields = [
      "year_started",
      "years_under_present_name",
      // "number_of_employees"
    ];
    intFields.forEach(f => {
      if (out[f] === "" || out[f] === undefined || out[f] === null) {
        delete out[f]; // Delete instead of setting to null to match boolean field behavior
      } else if (typeof out[f] === 'string') {
        // Convert string numbers to integers
        const parsed = parseInt(out[f], 10);
        out[f] = isNaN(parsed) ? null : parsed;
      } else if (typeof out[f] === 'number') {
        // Already a number, keep as is
        out[f] = out[f];
      }
    });

    const mapDurationToYears = (code) => {
      switch (code) {
        case "less_than_1_year": return "LT1";
        case "1_2_years": return "1_3";
        case "3_5_years": return "3_5";
        case "6_10_years": return "5_10";
        case "more_than_10_years": return "10P";
        default: return null;
      }
    };

    if (stepIndex === 0) {
      if (typeof out.phone_number === "string") {
        out.phone_number = out.phone_number.replace(/\D/g, "");
      }
    }
    if (stepIndex === 1) {
      // NOTE: business_address is sent as-is to backend, which maps it to business_address_line1 internally
      console.log("[VendorOnboarding] Step 1 business_address value:", out.business_address);
      if (typeof out.main_phone === "string") {
        out.main_phone = out.main_phone.replace(/\D/g, "");
      }
      if (typeof out.main_fax === "string") {
        out.main_fax = out.main_fax.replace(/\D/g, "");
      }
      // Transform county_ids to vendor_service_county_ids for backend
      if (out.county_ids !== undefined) {
        out.vendor_service_county_ids = out.county_ids;
        delete out.county_ids;
      }
      // Delete partnership_type if not a PARTNERSHIP
      if (out.organization_type !== "PARTNERSHIP") {
        delete out.partnership_type;
      } else if (out.partnership_type === "" || out.partnership_type === undefined) {
        // If it's a partnership but empty, also delete it
        delete out.partnership_type;
      }
      // Delete organization_other_description if not OTHER
      if (out.organization_type !== "OTHER") {
        delete out.organization_other_description;
      }
    }
    if (stepIndex === 2) {
      console.log("[VendorOnboarding] Step 2 contact fields BEFORE transform:", {
        business_contact_name: out.business_contact_name,
        business_contact_email: out.business_contact_email,
        business_contact_phone: out.business_contact_phone,
        insurance_contact_name: out.insurance_contact_name,
        insurance_contact_email: out.insurance_contact_email,
        insurance_contact_phone: out.insurance_contact_phone,
        accounting_contact_name: out.accounting_contact_name,
        accounting_contact_email: out.accounting_contact_email,
        accounting_contact_phone: out.accounting_contact_phone,
      });
      ["business_contact_phone", "insurance_contact_phone", "accounting_contact_phone"].forEach(f => {
        if (typeof out[f] === "string") out[f] = out[f].replace(/\D/g, "");
      });
      console.log("[VendorOnboarding] Step 2 contact fields AFTER transform:", {
        business_contact_name: out.business_contact_name,
        business_contact_email: out.business_contact_email,
        business_contact_phone: out.business_contact_phone,
        insurance_contact_name: out.insurance_contact_name,
        insurance_contact_email: out.insurance_contact_email,
        insurance_contact_phone: out.insurance_contact_phone,
        accounting_contact_name: out.accounting_contact_name,
        accounting_contact_email: out.accounting_contact_email,
        accounting_contact_phone: out.accounting_contact_phone,
      });
    }
    if (stepIndex === 3) {
      if (Array.isArray(out.licenses)) {
        out.licenses = out.licenses.map((l) => {
          const item = { ...l };
          if (item.expiration_date) {
            try {
              const d = new Date(item.expiration_date);
              if (!isNaN(d.getTime())) {
                const year = d.getUTCFullYear();
                const month = String(d.getUTCMonth() + 1).padStart(2, "0");
                const day = String(d.getUTCDate()).padStart(2, "0");
                item.expiration_date = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.error("Error formatting license expiration_date:", e);
            }
          }
          return item;
        });
      }
    }
    // Step 4 is Stripe (no transform needed)
    if (stepIndex === 5) {
      if (out.businessDuration !== undefined) {
        out.years_in_business = mapDurationToYears(out.businessDuration);
        delete out.businessDuration;
      }
      if (Array.isArray(out.references)) {
        out.references = out.references.map((ref) => ({
          ...ref,
          phone: typeof ref?.phone === "string" ? ref.phone.replace(/\D/g, "") : ref?.phone,
        }));
      }
    }
    if (stepIndex === 6) {
      // Delete bond fields if not bonded (consistent with other conditional field handling)
      if (out.is_bonded === false) {
        delete out.bond_amount;
        delete out.bond_expiration_date;
      } else if (out.bond_expiration_date) {
        // Only format date if bonded and date exists
        try {
          const d = new Date(out.bond_expiration_date);
          if (!isNaN(d.getTime())) {
            const year = d.getUTCFullYear();
            const month = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            out.bond_expiration_date = `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error("Error formatting bond_expiration_date:", e);
        }
      }
    }

    return out;
  };

  // Helper: build payload (partial for stepIndex, or full if stepIndex is null)
  const buildPayload = (formValues, stepIndex = null) => {
    const isFull = stepIndex === null;
    let raw = {};

    if (isFull) {
      // For full submission, take all fields from all steps
      const allFields = Object.values(stepFieldsMap).flat();
      allFields.forEach((key) => {
        raw[key] = formValues[key];
      });
    } else {
      // For partial submission, take only fields for the current step
      const fields = stepFieldsMap[stepIndex] || [];
      fields.forEach((key) => {
        raw[key] = formValues[key];
      });
    }

    // Transform keys/types for API
    let payload = { ...raw };
    if (isFull) {
      // Apply all step-based transformations
      for (let i = 0; i <= submitStep; i++) {
        payload = transformForApi(i, payload);
      }
    } else {
      payload = transformForApi(stepIndex, payload);
    }

    // Clean up empty arrays before sending to backend
    Object.keys(payload).forEach(key => {
      if (Array.isArray(payload[key]) && payload[key].length === 0) {
        delete payload[key];
      }
    });

    // Detect files after transform (including nested in arrays)
    const containsFile = (obj) => {
      if (obj instanceof File || obj instanceof Blob) return true;
      if (Array.isArray(obj)) return obj.some(containsFile);
      if (obj !== null && typeof obj === "object") {
        return Object.values(obj).some(containsFile);
      }
      return false;
    };
    let hasFile = containsFile(payload);

    if (hasFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v instanceof File || v instanceof Blob) {
          fd.append(k, v);
        } else if (Array.isArray(v)) {
          // Handle arrays (references, licenses, insurances)
          const processedArray = v.map((item, index) => {
            if (typeof item === "object" && item !== null) {
              const newItem = { ...item };
              Object.entries(item).forEach(([subK, subV]) => {
                if (subV instanceof File || subV instanceof Blob) {
                  const placeholder = `${k}_file_${index}_${subK}`;
                  fd.append(placeholder, subV);
                  newItem[subK] = placeholder;
                }
              });
              return newItem;
            }
            return item;
          });
          fd.append(k, JSON.stringify(processedArray));
        } else if (v !== null && typeof v === "object") {
          // For other complex structures
          fd.append(k, JSON.stringify(v));
        } else if (v === undefined || v === null) {
          fd.append(k, "");
        } else {
          fd.append(k, v);
        }
      });
      return fd;
    }
    return payload; // JSON body
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const body = buildPayload(values); // Build full payload with FormData if needed
      console.log("[VendorOnboarding] Final submit payload:", body);
      console.log("[VendorOnboarding] business_address in values:", values.business_address);
      if (body instanceof FormData) {
        console.log("[VendorOnboarding] Payload is FormData, logging entries:");
        for (let [key, value] of body.entries()) {
          console.log(`  ${key}:`, value);
        }
      } else {
        console.log("[VendorOnboarding] Payload business_address:", body.business_address);
      }
      await callApi(() => Vendor.vendorOnboarding(body, token), {
        onSuccess: (response) => {
          if (response.data) {
            setSubmitting(false);
            // Move to completion screen
            setActiveStep(7);
            // Clear draft from sessionStorage
            sessionStorage.removeItem("vendor_onboarding_draft");
          }
        },
        onError: (error) => {
          console.log(error);
          setSubmitting(false);
          setActiveStep((prevActiveStep) => prevActiveStep);
        },
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitting(false);
    }
  };

  const handleStepValidation = async (values, step) => {
    try {
      console.log(`[VendorOnboarding] Validating step ${step} with values:`, values);
      const schema = validationSchemas[step];
      if (schema) {
        await schema.validate(values, { abortEarly: false });
        console.log(`[VendorOnboarding] Step ${step} validation PASSED`);
      } else {
        console.log(`[VendorOnboarding] No validation schema found for step ${step}`);
      }
      return true;
    } catch (errors) {
      console.log(`[VendorOnboarding] Step ${step} validation FAILED:`, errors);
      if (errors.inner) {
        errors.inner.forEach(err => {
          console.log(` - ${err.path}: ${err.message}`);
        });
      }
      return false;
    }
  };

  // Keep URL step param <-> state in sync and support deep-linking
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get("step");
    if (stepParam !== null) {
      const parsed = parseInt(stepParam, 10);
      if (!Number.isNaN(parsed)) {
        const clamped = Math.max(0, Math.min(parsed, submitStep));
        if (clamped !== activeStep) {
          setActiveStep(clamped);
        }
      }
    }
    // If no step param, leave as default/state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="py-0">
      <div className="pt-[84px]">
        <Formik
          initialValues={{
            ...formInitialValues,
            // Prefer UserContext details when available
            fullName: userData?.fullName || userData?.full_name || formInitialValues.fullName,
            email: userData?.email || formInitialValues.email,
          }}
          enableReinitialize
          validationSchema={validationSchemas[activeStep]}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, setFieldValue, setValues }) => {
            // Log current state to help debug why next button is disabled
            console.log(`[VendorOnboarding] Render step ${activeStep}:`, {
              values,
              errors,
              touched,
              isSubmitting: values.isSubmitting // Formik state
            });
            

            // Wrapper to update both state and URL on step changes with partial save
            const setStepAndUrl = async (nextStepOrUpdater) => {
              let nextStepValue =
                typeof nextStepOrUpdater === "function"
                  ? nextStepOrUpdater(activeStep)
                  : nextStepOrUpdater;

              if (
                typeof nextStepValue !== "number" ||
                Number.isNaN(nextStepValue)
              ) {
                nextStepValue = activeStep; // fallback to current
              }

              const clamped = Math.max(0, Math.min(nextStepValue, submitStep));

              console.log(`[VendorOnboarding] setStepAndUrl: active=${activeStep}, target=${nextStepValue}, clamped=${clamped}`);

              // Determine navigation direction
              const movingForward = clamped > activeStep;

              // Save current step values only when moving forward
              if (movingForward) {
                console.log(`[VendorOnboarding] Attempting partial save for step ${activeStep} before advancing`);
                const body = buildPayload(values, activeStep);
                console.log(`[VendorOnboarding] Partial save payload for step ${activeStep}:`, body);
                if (body instanceof FormData) {
                  console.log("[VendorOnboarding] Payload is FormData, logging entries:");
                  for (let [key, value] of body.entries()) {
                    console.log(`  ${key}:`, value);
                  }
                }
                try {
                  await callApi(() => Vendor.vendorOnboardingPartial(body, token), {
                    onSuccess: async (res) => {
                      console.log("[VendorOnboarding] Partial save successful:", res?.data);
                      // Refresh latest snapshot so we always have server source of truth
                      try {
                        const res = await Vendor.getVendorOnboardingMe();
                        const snap = res?.data || {};
                        const merged = mapSnapshotToFormValues(snap, values);
                        // Preserve front-end only fields
                        merged.fullName = values.fullName || merged.fullName;
                        merged.email = merged.email || values.email || userData?.email || "";
                        setValues(merged);
                      } catch (_) {}
                    },
                    onError: (err) => {
                      console.error("[VendorOnboarding] Partial save failed:", err);
                    }
                  });
                } catch (e) {
                  console.error("[VendorOnboarding] Exception during partial save:", e);
                  // On forward navigation save failure, do not advance
                  return;
                }
              }

              setActiveStep(clamped);
              const params = new URLSearchParams(location.search);
              // Write 0-based value back to URL
              params.set("step", String(clamped));
              navigate(
                { pathname: location.pathname, search: params.toString() },
                { replace: true }
              );
            };

            return (
              <Form>
                <FormObserver />
                <StepperForm
                  stepsNames={stepsNames}
                  activeStep={activeStep}
                  setActiveStep={setStepAndUrl}
                  formData={values}
                  children={activeSteps[activeStep]}
                  footerText={false}
                  onStepValidation={handleStepValidation}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  stepFieldsMap={stepFieldsMap}
                  submitStep={submitStep}
                  hasOpenForm={hasOpenForm}
                >
                  <Box
                    sx={{
                      maxWidth: "840px",
                      borderRadius: "16px",
                      paddingY: { md: 3, xs: 2 },
                      paddingX: { md: 4, xs: 2 },
                      gap: 3,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid #ECECEC",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {activeStep === 7 && (
                        <img
                          className="md:h-[144px] md:w-[144px] h-[100px] w-[100px] mb-[32px]"
                          src={Submitted}
                          alt="Submitted"
                        />
                      )}
                      <Typography
                        variant="h1"
                        sx={{
                          color: "#242424",
                          lineHeight: "42px",
                          textAlign: "center",
                        }}
                      >
                        {Title[activeStep]?.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#595959",
                          fontSize: { md: "20px", xs: "16px" },
                          textAlign: "center",
                          lineHeight: "30px",
                        }}
                      >
                        {Title[activeStep]?.subTitle}
                      </Typography>
                    </Box>
                    {activeStep === 7 ? null : activeSteps[activeStep]}
                  </Box>
                </StepperForm>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default VendorOnboarding;
